/**
 * PR-40A — Audit sampling priority map.
 *
 * Reduces write pressure on `audit_master` by keeping 1:1 for critical
 * business/safety events and sampling everything else. Returns the
 * sample denominator N for an event_type (1 = always keep, 5 = keep 1
 * in 5, etc.). When an event is dropped, no insert is performed.
 *
 * Tuning targets ~80% write reduction at 500K concurrent without
 * losing visibility on revenue, fraud, or correctness signals.
 */
export type SampleRate = 1 | 5 | 20;

/** Always keep — webhook, money, fraud, correctness. */
const CRITICAL_EXACT = new Set<string>([
  "wa_share_completed",
  "wa_share_failed",
  "push_conversion",
  "first_prediction_completed",
  "first_referral_sent",
  "circuit_opened",
  "circuit_closed",
  "degraded_mode_enabled",
  "degraded_mode_disabled",
  "turnstile_failed",
  // PR-40A.1 — explicit critical prediction events (correctness / XP / settlement).
  "prediction_answer_submitted",
  "prediction_answer_settled",
  "prediction_correct",
  "prediction_xp_awarded",
  "prediction_error",
  "prediction_settlement_failed",
]);

const CRITICAL_PREFIXES = [
  "antifraud_",
  "xp_",
  "claim_",
  "auth_",
  "payment_",
  "webhook_",
  "sponsor_impression",
  "sponsor_click",
  "sponsor_conversion",
  "referral_",
  "voucher_",
  "reward_redeemed",
];

/** Retention/engagement — sample 1:5. */
const RETENTION_PREFIXES = [
  "push_sent",
  "push_opened",
  "push_suppressed",
  "wa_share_initiated",
  "wa_share_destination",
  "share_",
  "global_share",
  "war_room_",
  "cinematic_",
  "clip_",
  "viral_",
  "conversion_",
  "activation_",
];

/** Diagnostic/UI noise — sample 1:20. */
const DIAGNOSTIC_PREFIXES = [
  "ui_",
  "scroll_",
  "hover_",
  "focus_",
  "view_",
  "tick_",
  "tab_",
  "panel_",
  "ribbon_",
  "halftime_",
  "stadium_",
  "radar_",
  "master_qa_",
  "fallback_triggered",
];

/**
 * PR-40A.1 — UI-only / re-render noise. Forced to 1:20 regardless of any
 * other prefix match. These events were previously written 1:1 and produced
 * the bulk of `audit_master` insert pressure.
 */
const DIAGNOSTIC_EXACT = new Set<string>([
  // Prediction UI lifecycle (not correctness)
  "prediction_view",
  "prediction_auto_minimized",
  "prediction_card_auto_closed",
  "prediction_wow_shown",
  "prediction_to_share_seconds",
  "prediction_lifecycle",
  "prediction_dismissed",
  "prediction_reopened",
  // Pure UI / render noise
  "overlap_prevented",
  "theme_palette_applied",
  "background_render_paused",
  "poll_deduplicated",
  "match_ai_rich_context_loaded",
  "timeline_event_inserted",
  "home_feed_generated",
  "live_narrative_beat_emitted",
  "danger_play_activated",
  "h2h_insight_generated",
  "attention_focus_claimed",
  "edge_alert_triggered",
  "mobile_share_dock_shown",
  "live_commentary_generated",
]);

function startsWithAny(t: string, list: readonly string[]): boolean {
  for (const p of list) if (t.startsWith(p)) return true;
  return false;
}

/** Returns the sample denominator (1 | 5 | 20) for an event type. */
export function sampleRateFor(eventType: string): SampleRate {
  // UI / diagnostic exact matches take precedence over prefix rules.
  if (DIAGNOSTIC_EXACT.has(eventType)) return 20;
  if (CRITICAL_EXACT.has(eventType)) return 1;
  if (startsWithAny(eventType, CRITICAL_PREFIXES)) return 1;
  if (startsWithAny(eventType, RETENTION_PREFIXES)) return 5;
  if (startsWithAny(eventType, DIAGNOSTIC_PREFIXES)) return 20;
  // Default: keep 1:5 (business safe but not noisy).
  return 5;
}

/**
 * Returns true when the event should be persisted under the given
 * sample rate. Uses cheap pseudo-random (Math.random) — telemetry is
 * statistical, no need for crypto-grade randomness.
 */
export function shouldKeep(eventType: string): { keep: boolean; rate: SampleRate } {
  const rate = sampleRateFor(eventType);
  if (rate === 1) return { keep: true, rate };
  return { keep: Math.random() < 1 / rate, rate };
}